import {
  defineToggle,
  featureToggleClient,
  type FeatureToggleUser,
} from './toggle-client'
import {
  type Observable,
  combineLatestWith,
  concatMap,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  switchMap,
} from 'rxjs'
import { type User } from '@/features/iam/users/User'
import type { UserData } from '@/features/iam/users/UserData'
import { toStream } from '@/utils/mobx-util'
import { shareLatest } from '@/utils/rx-util'

/**
 * This contains our list of active toggles. Can be imported directly
 * by consumers.
 */
export const FeatureToggles = {
  bookkeeping: defineToggle('Portals.BookkeepingEnabled', false),
  documentRequest: defineToggle('Portals.DocumentRequestFlow', false),
  documentsApiV5: defineToggle('WorkPortal.UseDocumentsApiV5', false),
  emailConfirmationFlow: defineToggle('UseEmailConfirmationFlow', false),
  flockRelationshipManager: defineToggle(
    'Worklayer.UseFlockForJobRelationshipManager',
    false,
  ),
  jobNotes: defineToggle('Portals.JobNotesEnabled', false),
  jobTableDocumentTypeFilter: defineToggle(
    'WorkPortal.JobTableDocumentTypeFilterEnabled',
    false,
  ),
  multiFactorAuthentication: defineToggle(
    'Portals.MultiFactorAuthentication',
    false,
  ),
  newTerms: defineToggle('Portals.UseNewTerms', false),
  notesSupporting: defineToggle('Portals.NotesSupporting', false),
  pendingJobs: defineToggle('WorkPortal.PendingJobsEnabled', false),
  requireActivePtin: defineToggle('Worklayer.RequireActivePTIN', false),
  stripePaymentMethods: defineToggle('Portals.UseStripePaymentMethods', false),
  taxPlanIntegration: defineToggle(
    'Worklayer.TaxPlanWorklayerIntegrationEnabled',
    false,
  ),
  txfIntegration: defineToggle('Portals.TxfIntegrationEnabled', false),
  txfJobCard: defineToggle('Portals.TxfJobCardEnabled', false),
  updatedJobProgressionConfig: defineToggle(
    'HQ.UseUpdatedJobProgressionConfig',
    false,
  ),
}

/**
 * Options for {@link FeatureTogglesFeature}.
 */
export interface FeatureTogglesOptions {
  /**
   * Emits the current user when available.
   */
  currentUser$: Observable<User>

  /**
   * Emits the current user's data when available.
   */
  currentUserData$: Observable<UserData>
}

/**
 * Identifies the current user with the feature toggle client.
 */
export type FeatureTogglesFeature = ReturnType<typeof FeatureTogglesFeature>

export function FeatureTogglesFeature(options: FeatureTogglesOptions) {
  const currentUser$ = options.currentUser$.pipe(
    switchMap((user) =>
      toStream(() => user.isFullyLoaded, true).pipe(
        filter(Boolean),
        map(() => user),
      ),
    ),
    // e2e users are kept out of LaunchDarkly's user list.
    filter((user) => !user.email.includes('e2e-')),
    distinctUntilChanged(),
  )

  const featureToggleUsers$ = currentUser$.pipe(
    combineLatestWith(options.currentUserData$),
    map(([user, userData]): FeatureToggleUser => ({
      id: user.publicId!, // SAFETY: Fully loaded users have a public ID
      email: user.email,
      workspaceSlugs: [...userData.workspaceSlugs],
    })),
  )

  const featureToggleUserIdentified$ = featureToggleUsers$.pipe(
    concatMap((user) => featureToggleClient.identify(user)),
    shareLatest(),
  )

  // Subscribe to run the pipeline
  featureToggleUserIdentified$.subscribe()

  return {
    // Public
    /**
     * Emits when the feature toggle user has been identified.
     */
    featureToggleUserIdentified$,

    /**
     * Resolves when the feature toggle user has been identified.
     */
    featureToggleUserIdentified: firstValueFrom<unknown, unknown>(
      featureToggleUserIdentified$,
      { defaultValue: null },
    ),
  }
}
